import WebApp from '@twa-dev/sdk';

import { Suspense, lazy, useEffect, useRef, useState } from 'react';

import { useAtomValue, useSetAtom } from 'jotai';
import { AlertCircle, Clock, Loader2, Lock, RefreshCw } from 'lucide-react';
import {
  BrowserRouter,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import BottomBar from '@/components/bottom-bar';
import { Button } from '@/components/ui/button';
import {
  useEventTracking,
  usePageTracking,
  useUserTracking,
} from '@/hooks/useAnalytics';
import {
  clickerAtom,
  isLoadingAtom,
  isServerErrorAtom,
  userAtom,
} from '@/jotai/app';
import BackendService from '@/services/backend.service';

import { usePagePerformance } from './hooks/usePerformance';

const Calendar = lazy(() => import('@/routes/calendar'));
const Challenges = lazy(() => import('@/routes/challenges'));
const Challenge = lazy(() => import('@/routes/challenge'));
const Leaderboard = lazy(() => import('@/routes/leaderboard'));
const IndividualChallengeLeaderboard = lazy(
  () => import('@/routes/individual-challenge-leaderboard')
);
const Coin = lazy(() => import('@/routes/coin'));

const maintenance = false;

function ScrollToTop({ ref }: { ref: React.RefObject<HTMLDivElement | null> }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  usePageTracking();
  usePagePerformance();

  useEffect(() => {
    if (ref.current) {
      ref.current.scrollTo(0, 0);
    }
  }, [pathname, ref]);

  useEffect(() => {
    const hasNavigated = sessionStorage.getItem('hasNavigatedToChallenge');
    if (
      !hasNavigated &&
      WebApp.initDataUnsafe.start_param?.startsWith('openChallenge-')
    ) {
      const challengeId = WebApp.initDataUnsafe.start_param
        .split('-')[1]
        .trim();
      navigate(`/challenges/${challengeId}`);
      sessionStorage.setItem('hasNavigatedToChallenge', 'true');
    }
  }, [navigate]);

  return null;
}

function App() {
  const { isLoading, isServerError, platform } = useAtomValue(userAtom);
  const setClicker = useSetAtom(clickerAtom);
  const setIsLoading = useSetAtom(isLoadingAtom);
  const setIsServerError = useSetAtom(isServerErrorAtom);
  const [isLandscape, setIsLandscape] = useState(
    window.matchMedia('(orientation: landscape)').matches
  );

  const mainDiv = useRef<HTMLDivElement>(null);

  const { setUser } = useUserTracking();
  const { trackErrorEvent } = useEventTracking();

  useEffect(() => {
    const initializeApp = async () => {
      try {
        const clicker = await BackendService.getClicker();
        setClicker(clicker);

        if (clicker?.telegram_id) {
          setUser(clicker.telegram_id.toString());
        }

        setIsLoading(false);
      } catch (error) {
        console.error('Failed to initialize app:', error);
        trackErrorEvent('App initialization failed', 'INIT_ERROR');
        setIsServerError(true);
      } finally {
        setIsLoading(false);
      }
    };

    initializeApp();
  }, [setClicker, setIsLoading, setIsServerError, setUser, trackErrorEvent]);

  useEffect(() => {
    WebApp.setHeaderColor('#000000');
    WebApp.setBackgroundColor('#000000');
    WebApp.setBottomBarColor('#000000');
    WebApp.enableClosingConfirmation();
    WebApp.disableVerticalSwipes();
    WebApp.expand();
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(orientation: landscape)');
    const handleOrientationChange = (e: MediaQueryListEvent) => {
      setIsLandscape(e.matches);
    };

    mediaQuery.addEventListener('change', handleOrientationChange);
    return () =>
      mediaQuery.removeEventListener('change', handleOrientationChange);
  }, []);

  if (platform === 'unknown' && !import.meta.env.DEV) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <Lock className="w-10 h-10" />
      </div>
    );
  }

  if (isLandscape) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-6 px-4">
        <AlertCircle className="w-16 h-16 text-red-500 animate-pulse" />
        <div className="flex flex-col items-center gap-2">
          <h2 className="text-xl font-semibold text-red-500">
            Please turn your device vertically
          </h2>
          <p className="text-gray-400 text-center">
            TradyFi's best experience on this device is in portrait mode.
          </p>
        </div>
      </div>
    );
  }

  if (maintenance) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-6 px-5">
        <Clock className="w-16 h-16 text-red-500 animate-pulse" />
        <div className="flex flex-col items-center gap-2">
          <h2 className="text-xl font-semibold text-red-500">
            Under Maintenance
          </h2>
          <p className="text-gray-400 text-center">
            We're currently performing maintenance. Please check back later.
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <Loader2 className="w-20 h-20 animate-spin" />
      </div>
    );
  }

  if (isServerError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-6 px-4">
        <AlertCircle className="w-16 h-16 text-red-500 animate-pulse" />
        <div className="flex flex-col items-center gap-2">
          <h2 className="text-xl font-semibold text-red-500">
            Connection Error
          </h2>
          <p className="text-gray-400 text-center">
            Failed to connect to server. Please check your connection and try
            again.
          </p>
        </div>
        <Button
          onClick={() => window.location.reload()}
          className="flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Retry Connection
        </Button>
      </div>
    );
  }

  return (
    <>
      <div
        ref={mainDiv}
        className="w-full h-[100vh] px-4 pt-0 pb-[120px] overflow-y-scroll"
      >
        <BrowserRouter>
          <ScrollToTop ref={mainDiv} />
          <Suspense
            fallback={
              <div className="flex flex-col items-center justify-center min-h-screen gap-4">
                <Loader2 className="w-20 h-20 animate-spin" />
              </div>
            }
          >
            <Routes>
              <Route
                path="/"
                element={<Challenges />}
              />
              <Route
                path="/leaderboard"
                element={<Leaderboard />}
              />
              <Route
                path="/calendar"
                element={<Calendar />}
              />
              <Route
                path="/challenges/:id"
                element={<Challenge />}
              />
              <Route
                path="/challenges/:challengeId/leaderboard"
                element={<IndividualChallengeLeaderboard />}
              />
              <Route
                path="/challenges/:challengeId/coin/:coinId"
                element={<Coin />}
              />
            </Routes>
          </Suspense>
          <div className="fixed bottom-0 left-0 right-0 bottom-bar-container">
            <BottomBar />
          </div>
        </BrowserRouter>
      </div>
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        closeButton={false}
        rtl={false}
        pauseOnFocusLoss
        draggable={false}
        pauseOnHover
      />
    </>
  );
}

export default App;
